import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
    Empty,
    Spin,
    Select,
    Modal,
    Button,
    Input,
    InputNumber,
    DatePicker,
    Switch,
    Checkbox,
    message,
    Tag,
    Tooltip,
    Icon
} from "antd";
import SectionBlock from "./components/section-block";
import CardBlock from "./components/card-block";
import PortfolioTable from "./components/portfolio-table";
import { SECTION_IDS } from "./constants";
import {
    BUREAU_LOAN_COLUMNS,
    BUREAU_LOAN_STICKY,
    FORMAT_DASH,
    sumLoans
} from "./bureau-loan-builder";
import { MONEY_UNIT_OPTIONS, formatMoney } from "./money-format";
import {
    createBureauLoanAccount,
    updateBureauLoanAccount,
    editBureauLoanAccount
} from "services/case-service";

const { Option } = Select;

// Server-side loan filter (bureau-overview/v2 `loan_filter` param). Default is
// non-Progfin so reviewers see external obligations first.
const LOAN_FILTER_OPTIONS = [
    { value: "non_progfin_loans", label: "Non-Progfin loans" },
    { value: "progfin_loans", label: "Progfin loans" },
    { value: "all_loans", label: "All loans" }
];

// Ownership indicator codes (TransUnion) offered in the add-loan form. Joint
// accounts (4) carry a 0.5 obligation factor; everything else is 1.0.
const OWNERSHIP_OPTIONS = [
    { value: "1", label: "Individual" },
    { value: "2", label: "Authorized User" },
    { value: "3", label: "Guarantor" },
    { value: "4", label: "Joint" }
];

// Nature of loan = bureau tagging bucket. Mirrors the backend
// _TAGGING_ENUM_TO_LABEL; the label is mapped back to the BureauTagging enum
// server-side (it drives the displayed "Nature of Loan" column).
const NATURE_OF_LOAN_OPTIONS = [
    { value: "USL/BL", label: "USL/BL" },
    { value: "CC/OD", label: "CC/OD" },
    { value: "Secured", label: "Secured" },
    { value: "Gold Loans", label: "Gold Loans" },
    { value: "Others", label: "Others" }
];

// Empty add-loan form (fields mirror one table row).
// Repayment frequency (backend generics.utils.enums.PaymentFrequency). Sent as
// the integer enum value; UNDEFINED (0) omitted so it reads as "not selected".
const PAYMENT_FREQUENCY_OPTIONS = [
    { value: 1, label: "Weekly" },
    { value: 2, label: "Fortnightly" },
    { value: 3, label: "Monthly" },
    { value: 4, label: "Quarterly" },
    { value: 5, label: "Bullet payment" },
    { value: 6, label: "Daily" },
    { value: 7, label: "Half yearly" },
    { value: 8, label: "Yearly" },
    { value: 9, label: "On-demand" }
];

const EMPTY_FORM = {
    lender_name: "",
    nature_of_loan: "",
    ownership_indicator: "1",
    ownership_factor: 1,
    payment_frequency: null,
    sanction_amount: null,
    outstanding: null,
    overdue: 0,
    emi_monthly_obligation: null,
    loan_start_date: null,
    has_obligation: true,
    is_closed: false
};

// Sums the last-3-months enquiry count across ALL applicants in the summary
// map. `summary` is a { [applicantId]: rows } object built by
// buildBureauSummaries; each row carries an `m3` field for l3_months. The
// "Total" row already aggregates categories per applicant, so summing those
// avoids double-counting on the per-category rows.
function totalEnquiries3m(summary) {
    if (!summary || typeof summary !== "object") return 0;
    let sum = 0;
    Object.keys(summary).forEach(applicantId => {
        const rows = summary[applicantId] || [];
        const totalRow = rows.find(r => r && r.isTotal);
        if (totalRow && Number.isFinite(Number(totalRow.m3))) {
            sum += Number(totalRow.m3);
        }
    });
    return sum;
}

// Renders KPI tiles above the per-applicant tables: number of open loans,
// total current outstanding, total monthly EMI obligation, and total bureau
// enquiries in the last 3 months. Money values follow the section-level unit
// toggle.
function OverallTiles({ overall, unit, summary }) {
    if (!overall) {
        return null;
    }
    const enquiries3m = totalEnquiries3m(summary);
    const tiles = [
        { label: "Open Loans", value: overall.loanCount, format: "raw" },
        { label: "Total Outstanding", value: overall.outstanding, format: "money" },
        {
            label: "Total Monthly EMI",
            value: overall.emi_monthly_obligation,
            format: "money"
        },
        {
            label: "Enquiries (Last 3M)",
            value: enquiries3m,
            format: "raw"
        }
    ];
    return (
        <div className={"cc-bl-tiles"}>
            {tiles.map(t => (
                <div key={t.label} className={"cc-bl-tile"}>
                    <div className={"cc-bl-tile__label"}>{t.label}</div>
                    <div className={"cc-bl-tile__value"}>
                        {t.format === "money"
                            ? formatMoney(t.value, unit) || FORMAT_DASH
                            : t.value || 0}
                    </div>
                </div>
            ))}
        </div>
    );
}

OverallTiles.propTypes = {
    overall: PropTypes.object,
    unit: PropTypes.string,
    // { [applicantId]: rows } — reads `.m3` off the isTotal row per applicant.
    summary: PropTypes.object
};

// Money cell for the summary table: ₹0 for zero, unit-formatted otherwise.
function summaryMoney(value, unit) {
    if (!value) {
        return "₹0";
    }
    return formatMoney(value, unit) || FORMAT_DASH;
}

// Combined Loans + Enquiries by-type table (mirrors the Credit Brief PDF's
// second table). The 8 Loans columns and 5 Enquiries columns are tinted with
// two on-theme blues so the concatenated blocks read as distinct sections.
function BureauSummaryTable({ rows, unit }) {
    if (!rows || !rows.length || !rows.some(r => !r.isTotal)) {
        return null;
    }
    const loanCols = [
        { key: "loans3m", label: "#3m" },
        { key: "loans12m", label: "#12m" },
        { key: "sanction", label: "Sanction", money: true },
        { key: "outstanding", label: "O/S", money: true },
        { key: "overdue", label: "Overdue", money: true },
        { key: "dpd306m", label: "DPD30+ 6m" },
        { key: "dpd9012m", label: "DPD90+ 12m" },
        { key: "settlement", label: "Settlement", money: true }
    ];
    const enqCols = [
        { key: "d7", label: "7d" },
        { key: "d30", label: "30d" },
        { key: "m3", label: "3m" },
        { key: "m6", label: "6m" },
        { key: "gt6", label: ">6m" }
    ];
    const cell = (r, col, band) => (
        <td key={col.key} className={"cc-bl-sum__" + band}>
            {col.money ? summaryMoney(r[col.key], unit) : r[col.key]}
        </td>
    );
    return (
        <div className={"cc-bl-sum-wrap"}>
            <table className={"cc-bl-sum"}>
                <thead>
                    <tr>
                        <th className={"cc-bl-sum__type"} rowSpan={2}>
                            Type
                        </th>
                        <th className={"cc-bl-sum__ln"} colSpan={loanCols.length}>
                            Loans
                        </th>
                        <th className={"cc-bl-sum__enq"} colSpan={enqCols.length}>
                            Enquiries
                        </th>
                    </tr>
                    <tr>
                        {loanCols.map(c => (
                            <th key={c.key} className={"cc-bl-sum__ln"}>
                                {c.label}
                            </th>
                        ))}
                        {enqCols.map(c => (
                            <th key={c.key} className={"cc-bl-sum__enq"}>
                                {c.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <tr
                            key={r.category}
                            className={r.isTotal ? "cc-bl-sum__total" : ""}
                        >
                            <td className={"cc-bl-sum__type"}>{r.category}</td>
                            {loanCols.map(c => cell(r, c, "ln"))}
                            {enqCols.map(c => cell(r, c, "enq"))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

BureauSummaryTable.propTypes = {
    rows: PropTypes.array,
    unit: PropTypes.string
};

/**
 * Current Loan Obligation section — external loans the applicants currently
 * carry, sourced from the latest bureau pull (CIBIL). One card per applicant:
 *   - lender, nature, ownership, sanction, current outstanding, EMI, start date
 *   - an editable "Obligation" (Yes/No) toggle per row
 *   - per-applicant totals row at the bottom of each table
 *   - an "Add Loan" action to append a manual row
 * Plus an aggregate KPI strip across all applicants at the top.
 */
class BureauLoanSection extends React.Component {
    state = {
        unit: "lacs",
        // add / edit loan modal
        modalVisible: false,
        modalApplicantId: null,
        // id of the loan row being edited (null when adding a new one)
        editingId: null,
        submitting: false,
        form: { ...EMPTY_FORM },
        // id of the row whose obligation toggle is mid-flight
        togglingId: null,
        // Loans the analyst has unchecked, keyed by row `_selectKey`. Presence
        // means excluded — so every row starts checked without seeding state.
        // View-only: nothing here is persisted or sent to the API.
        excludedLoans: {}
    };

    // Refresh CIBIL / Add Loan are only actionable at the credit stages —
    // same gate used elsewhere in Credit Checker (TVR reset, bank statement
    // rebuild).
    canEdit = () => [7, 8].includes(Number(this.props.stageId));

    setUnit = unit => this.setState({ unit });

    // Include/exclude one loan from its applicant's total row.
    toggleLoanIncluded = (selectKey, included) =>
        this.setState(prev => {
            const excludedLoans = { ...prev.excludedLoans };
            if (included) {
                delete excludedLoans[selectKey];
            } else {
                excludedLoans[selectKey] = true;
            }
            return { excludedLoans };
        });

    // ── obligation toggle ──────────────────────────────────────────────────
    handleToggleObligation = (row, checked) => {
        if (!row || !row.id || !this.props.caseId) {
            return;
        }
        this.setState({ togglingId: row.id });
        updateBureauLoanAccount(this.props.caseId, {
            id: row.id,
            has_obligation: checked
        }).then(
            () => {
                // Re-read from the server first: the GET is the only source of
                // truth for the table, so it must fire even if the toast or a
                // re-render below throws.
                if (this.props.onRefresh) {
                    this.props.onRefresh();
                }
                this.setState({ togglingId: null });
                message.success("Obligation updated");
            },
            // Second arg (not .catch) so a failure *after* a successful write
            // is never reported as a failed write.
            () => {
                message.error("Failed to update obligation");
                this.setState({ togglingId: null });
            }
        );
    };

    // Columns = the read-only bureau columns + an editable Obligation column.
    columns = () => {
        const obligation = {
            key: "has_obligation",
            label: "Obligation",
            render: (value, row) => {
                // The loan-detail rows are obligations by default; treat a
                // missing/null has_obligation as Yes so the toggle reflects that.
                const checked =
                    value === undefined || value === null
                        ? true
                        : Boolean(value);
                return (
                    <Switch
                        size={"small"}
                        checkedChildren={"Yes"}
                        unCheckedChildren={"No"}
                        checked={checked}
                        disabled={!row.id || !this.props.caseId}
                        loading={this.state.togglingId === row.id}
                        onChange={c => this.handleToggleObligation(row, c)}
                    />
                );
            }
        };
        // Per-row Edit action — opens the loan form pre-filled and PUTs the
        // full editable field set on save.
        const editAction = {
            key: "_edit",
            // Frozen alongside the identity columns, so it needs a real width
            // for their left offsets to add up (a shrink-to-content width:1
            // would leave the pinned block misaligned).
            width: 44,
            label: "",
            render: (value, row) =>
                row.id ? (
                    <Button
                        size={"small"}
                        icon={"edit"}
                        type={"link"}
                        aria-label={"Edit loan"}
                        title={"Edit loan"}
                        disabled={!this.props.caseId}
                        onClick={() => this.openEditModal(row)}
                    />
                ) : null
        };
        // Per-row include checkbox — checked by default; unchecking drops the
        // row from the applicant total below. Display-only, nothing is saved.
        const includeInTotal = {
            key: "_select",
            // Frozen alongside the identity columns, so it needs a real width
            // for their left offsets to add up (see editAction below).
            width: 40,
            label: "",
            render: (value, row) => (
                <Checkbox
                    checked={!this.state.excludedLoans[row._selectKey]}
                    aria-label={"Include this loan in the applicant total"}
                    title={"Include in applicant total"}
                    onChange={e =>
                        this.toggleLoanIncluded(
                            row._selectKey,
                            e.target.checked
                        )
                    }
                />
            )
        };
        return [
            includeInTotal,
            editAction,
            ...BUREAU_LOAN_COLUMNS,
            obligation
        ];
    };

    // ── add / edit loan modal ──────────────────────────────────────────────
    openAddModal = applicantId =>
        this.setState({
            modalVisible: true,
            modalApplicantId: applicantId,
            editingId: null,
            form: { ...EMPTY_FORM }
        });

    // Pre-fill the form from an existing loan row and open in edit mode.
    openEditModal = row =>
        this.setState({
            modalVisible: true,
            modalApplicantId: row.applicant_id || null,
            editingId: row.id,
            form: {
                lender_name: row.lender_name || "",
                nature_of_loan: row.nature_of_loan || "",
                ownership_indicator: row.ownership_indicator || "1",
                ownership_factor:
                    row.ownership_indicator === "4" ? 0.5 : 1,
                sanction_amount: row.sanction_amount,
                outstanding: row.outstanding,
                overdue: row.overdue || 0,
                emi_monthly_obligation: row.emi_monthly_obligation,
                loan_start_date: row.loan_start_date
                    ? moment(row.loan_start_date, "YYYY-MM-DD")
                    : null,
                has_obligation:
                    row.has_obligation === undefined ||
                    row.has_obligation === null
                        ? true
                        : Boolean(row.has_obligation),
                is_closed: Boolean(row.is_closed)
            }
        });

    closeAddModal = () =>
        this.setState({ modalVisible: false, submitting: false, editingId: null });

    setField = (key, value) =>
        this.setState(prev => ({ form: { ...prev.form, [key]: value } }));

    setOwnership = value =>
        this.setState(prev => ({
            form: {
                ...prev.form,
                ownership_indicator: value,
                // Joint (4) → 0.5 obligation factor, else 1.0.
                ownership_factor: value === "4" ? 0.5 : 1
            }
        }));

    // Shared loan-detail fields for both create (POST) and edit (PUT).
    formToFields = form => ({
        lender_name: form.lender_name,
        // Sent as `tagging` — the server maps this label to the BureauTagging
        // enum that drives the displayed "Nature of Loan" column. (The model's
        // nature_of_loan free-text name is left untouched on edit.)
        tagging: form.nature_of_loan,
        has_obligation: Boolean(form.has_obligation),
        ownership_indicator: form.ownership_indicator,
        ownership_factor: form.ownership_factor,
        sanction_amount: form.sanction_amount,
        outstanding: form.outstanding,
        overdue: form.overdue,
        emi_monthly_obligation: form.emi_monthly_obligation,
        loan_start_date: form.loan_start_date
            ? moment(form.loan_start_date).format("YYYY-MM-DD")
            : null,
        is_closed: Boolean(form.is_closed)
    });

    handleSubmit = () => {
        const { form, modalApplicantId, editingId } = this.state;
        if (!this.props.caseId) {
            return;
        }
        if (!form.lender_name) {
            message.warning("Lender is required");
            return;
        }
        const fields = this.formToFields(form);
        this.setState({ submitting: true });

        // Edit an existing row → PUT with its id; else create a new one → POST.
        const request = editingId
            ? editBureauLoanAccount(this.props.caseId, {
                id: editingId,
                ...fields
            })
            : createBureauLoanAccount(this.props.caseId, {
                applicants_id: modalApplicantId,
                ...fields,
                // Payment frequency is captured only on create (integer enum;
                // 0 = Undefined when left unselected). Not sent on edit.
                payment_frequency:
                    form.payment_frequency == null
                        ? 0
                        : form.payment_frequency
            });

        request.then(
            () => {
                // Re-read from the server first: the GET is the only source of
                // truth for the table, so it must fire even if the toast or a
                // re-render below throws.
                if (this.props.onRefresh) {
                    this.props.onRefresh();
                }
                this.setState({
                    modalVisible: false,
                    submitting: false,
                    editingId: null
                });
                message.success(editingId ? "Loan updated" : "Loan added");
            },
            // Second arg (not .catch) so a failure *after* a successful write
            // is never reported as a failed write.
            () => {
                message.error(
                    editingId ? "Failed to update loan" : "Failed to add loan"
                );
                this.setState({ submitting: false });
            }
        );
    };

    renderField = (label, node) => (
        <div className={"cc-bl-field"} style={{ marginBottom: 12 }}>
            <div
                style={{
                    fontSize: 12,
                    color: "#5f6470",
                    marginBottom: 4,
                    fontWeight: 600
                }}
            >
                {label}
            </div>
            {node}
        </div>
    );

    renderAddModal = () => {
        const { form } = this.state;
        return (
            <Modal
                title={
                    this.state.editingId
                        ? "Edit Loan Account"
                        : "Add Loan Account"
                }
                visible={this.state.modalVisible}
                onOk={this.handleSubmit}
                okText={this.state.editingId ? "Save" : "Add"}
                confirmLoading={this.state.submitting}
                onCancel={this.closeAddModal}
                destroyOnClose
            >
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        columnGap: 14
                    }}
                >
                    {this.renderField(
                        "Lender",
                        <Input
                            value={form.lender_name}
                            onChange={e =>
                                this.setField("lender_name", e.target.value)
                            }
                            placeholder={"e.g. HDFC Bank"}
                        />
                    )}
                    {this.renderField(
                        "Nature of Loan",
                        <Select
                            style={{ width: "100%" }}
                            value={form.nature_of_loan || undefined}
                            placeholder={"Select nature of loan"}
                            onChange={v => this.setField("nature_of_loan", v)}
                        >
                            {NATURE_OF_LOAN_OPTIONS.map(o => (
                                <Option key={o.value} value={o.value}>
                                    {o.label}
                                </Option>
                            ))}
                        </Select>
                    )}
                    {this.renderField(
                        "Ownership",
                        <Select
                            style={{ width: "100%" }}
                            value={form.ownership_indicator}
                            onChange={this.setOwnership}
                        >
                            {OWNERSHIP_OPTIONS.map(o => (
                                <Option key={o.value} value={o.value}>
                                    {o.label}
                                </Option>
                            ))}
                        </Select>
                    )}
                    {this.renderField(
                        "Payment Frequency",
                        <Select
                            style={{ width: "100%" }}
                            placeholder={"Select frequency"}
                            value={form.payment_frequency}
                            onChange={v =>
                                this.setField("payment_frequency", v)
                            }
                            allowClear
                        >
                            {PAYMENT_FREQUENCY_OPTIONS.map(o => (
                                <Option key={o.value} value={o.value}>
                                    {o.label}
                                </Option>
                            ))}
                        </Select>
                    )}
                    {this.renderField(
                        "Loan Start Date",
                        <DatePicker
                            style={{ width: "100%" }}
                            value={form.loan_start_date}
                            format={"DD/MM/YYYY"}
                            onChange={v => this.setField("loan_start_date", v)}
                        />
                    )}
                    {this.renderField(
                        "Sanction Amount",
                        <InputNumber
                            style={{ width: "100%" }}
                            min={0}
                            value={form.sanction_amount}
                            onChange={v =>
                                this.setField("sanction_amount", v)
                            }
                        />
                    )}
                    {this.renderField(
                        "Current OS",
                        <InputNumber
                            style={{ width: "100%" }}
                            min={0}
                            value={form.outstanding}
                            onChange={v => this.setField("outstanding", v)}
                        />
                    )}
                    {this.renderField(
                        "Overdue",
                        <InputNumber
                            style={{ width: "100%" }}
                            min={0}
                            value={form.overdue}
                            onChange={v => this.setField("overdue", v)}
                        />
                    )}
                    {this.renderField(
                        "Monthly EMI",
                        <InputNumber
                            style={{ width: "100%" }}
                            min={0}
                            value={form.emi_monthly_obligation}
                            onChange={v =>
                                this.setField("emi_monthly_obligation", v)
                            }
                        />
                    )}
                    {this.renderField(
                        "Obligation",
                        <Switch
                            checkedChildren={"Yes"}
                            unCheckedChildren={"No"}
                            checked={Boolean(form.has_obligation)}
                            onChange={v => this.setField("has_obligation", v)}
                        />
                    )}
                    {this.renderField(
                        "Closed",
                        <Switch
                            checkedChildren={"Yes"}
                            unCheckedChildren={"No"}
                            checked={Boolean(form.is_closed)}
                            onChange={v => this.setField("is_closed", v)}
                        />
                    )}
                </div>
            </Modal>
        );
    };

    // CIBIL re-pull is offered once the last pull is older than this many days.
    static CIBIL_REPULL_DAYS = 30;

    // Confirm before re-pulling CIBIL: the pull hits the bureau and counts
    // against the applicant's monthly pull limit, so it should never fire on a
    // stray click.
    confirmTriggerCibil = (applicant, pulledOn) => {
        Modal.confirm({
            title: "Pull CIBIL for this applicant?",
            content: (
                <div>
                    <p style={{ marginBottom: 8 }}>
                        A fresh CIBIL report will be pulled for{" "}
                        <strong>{applicant.applicantName}</strong>.
                    </p>
                    <p style={{ marginBottom: 8 }}>
                        {pulledOn
                            ? `Last pulled on ${pulledOn.format("DD MMM YYYY")}.`
                            : "No CIBIL has been pulled for this applicant yet."}
                    </p>
                    <p style={{ marginBottom: 0 }}>
                        This counts against the monthly pull limit.
                    </p>
                </div>
            ),
            okText: "Pull CIBIL",
            cancelText: "Cancel",
            onOk: () => this.props.onTriggerCibil(applicant.applicantId)
        });
    };

    renderApplicant = applicant => {
        const { unit, excludedLoans } = this.state;
        // Stable per-row key for the include checkbox: the loan id once the row
        // is persisted, else its position within this applicant.
        const loans = (applicant.loans || []).map((loan, i) => ({
            ...loan,
            _selectKey: `${applicant.applicantId}:${
                loan.id != null ? loan.id : `i${i}`
            }`
        }));
        // The total row reflects only the checked rows — every row starts
        // checked, so this matches the builder's totals until one is unchecked.
        const totals = sumLoans(
            loans.filter(loan => !excludedLoans[loan._selectKey])
        );
        // CIBIL last-pull state for this applicant.
        const pulledOn = applicant.bureauCreateDate
            ? moment(applicant.bureauCreateDate)
            : null;
        const pulledValid = pulledOn && pulledOn.isValid();
        const daysSincePull = pulledValid ? moment().diff(pulledOn, "days") : null;
        // Re-pull allowed when never pulled, or the last pull is stale (>30d).
        const canTriggerCibil =
            !pulledValid || daysSincePull > BureauLoanSection.CIBIL_REPULL_DAYS;
        const triggeringCibil =
            this.props.triggeringCibilId === applicant.applicantId;
        // Applicant totals rendered as the table's footer row (aligned under the
        // Sanction / Outstanding / Monthly EMI columns).
        const totalRow =
            applicant.loanCount > 0
                ? {
                    _label: "Applicant total",
                    sanction_amount: totals.sanction_amount,
                    outstanding: totals.outstanding,
                    overdue: totals.overdue,
                    emi_monthly_obligation: totals.emi_monthly_obligation
                }
                : null;
        return (
            <CardBlock
                key={applicant.applicantId || applicant.applicantName}
                icon={"user"}
                title={
                    <span>
                        {applicant.applicantName}
                        {applicant.bureauScore !== null &&
                        applicant.bureauScore !== undefined ? (
                                <Tag
                                    className={"cc-bl-score"}
                                    color={"blue"}
                                    style={{ marginLeft: 8 }}
                                    title={"Bureau score"}
                                >
                                    <Icon type={"dashboard"} />{" "}
                                    {applicant.bureauScore}
                                </Tag>
                            ) : null}
                        {applicant.isMainBorrower ? (
                            <Tag
                                className={"cc-bl-main-borrower"}
                                color={"green"}
                                style={{ marginLeft: 4 }}
                                title={"Main borrower"}
                            >
                                <Icon type={"user"} /> Main Borrower
                            </Tag>
                        ) : null}
                        {/* When the bureau was last pulled. Amber once it is
                            older than the re-pull window so it reads as stale.
                            Colours set via class (the antd "default" tag renders
                            near-invisible light-on-light in this theme). */}
                        <Tag
                            className={
                                "cc-bl-cibil-date" +
                                (canTriggerCibil
                                    ? " cc-bl-cibil-date--stale"
                                    : "")
                            }
                            style={{ marginLeft: 4 }}
                            title={"CIBIL last pulled"}
                        >
                            <Icon type={"clock-circle"} />{" "}
                            {pulledValid
                                ? `CIBIL pulled ${pulledOn.format("DD MMM YYYY")}`
                                : "CIBIL not pulled"}
                        </Tag>
                    </span>
                }
                subtitle={`${applicant.loanCount} open loan${
                    applicant.loanCount === 1 ? "" : "s"
                }`}
                action={
                    <span className={"cc-bl-actions"}>
                        {/* Refresh CIBIL — always offered next to the score. The
                            backend enforces the pull limit (one fresh pull a
                            month, plus one within-month retry when the last score
                            was bad) and returns a flagged response when the limit
                            is hit; the parent handles that. Same consumer trigger
                            the Case Details tab fires. */}
                        {this.props.onTriggerCibil ? (
                            <Tooltip
                                title={
                                    pulledValid
                                        ? `CIBIL last pulled ${daysSincePull} day${
                                            daysSincePull === 1 ? "" : "s"
                                        } ago.`
                                        : "No CIBIL on file — pull it now."
                                }
                            >
                                <Button
                                    size={"small"}
                                    type={"primary"}
                                    icon={"reload"}
                                    loading={triggeringCibil}
                                    disabled={
                                        !this.props.caseId ||
                                        !applicant.applicantId ||
                                        !this.canEdit()
                                    }
                                    onClick={() =>
                                        this.confirmTriggerCibil(
                                            applicant,
                                            pulledValid ? pulledOn : null
                                        )
                                    }
                                >
                                    Refresh CIBIL
                                </Button>
                            </Tooltip>
                        ) : null}
                        <Button
                            size={"small"}
                            icon={"plus"}
                            disabled={!this.props.caseId || !this.canEdit()}
                            onClick={() =>
                                this.openAddModal(applicant.applicantId)
                            }
                        >
                            Add Loan
                        </Button>
                    </span>
                }
            >
                <PortfolioTable
                    columns={this.columns()}
                    rows={loans}
                    unit={unit}
                    totalRow={totalRow}
                    stickyCount={BUREAU_LOAN_STICKY}
                    emptyText={"No open loans found in bureau"}
                />
                <BureauSummaryTable
                    rows={(this.props.summary || {})[applicant.applicantId]}
                    unit={unit}
                />
            </CardBlock>
        );
    };

    render() {
        const {
            innerRef,
            data,
            loading,
            expanded,
            onToggle,
            loanFilter,
            onLoanFilterChange,
            includeClosed,
            onIncludeClosedChange,
            eligibilityCalculator
        } = this.props;
        const hasData = data && data.applicants && data.applicants.length;
        return (
            <SectionBlock
                id={SECTION_IDS.BUREAU_LOAN}
                innerRef={innerRef}
                title={"Current Loan Obligation"}
                subtitle={
                    "External loans currently active across all applicants (from the latest bureau pull)"
                }
                icon={"credit-card"}
                expanded={expanded}
                onToggle={onToggle}
                extra={
                    <span className={"cc-bl-controls"}>
                        <span className={"cc-pf-unit"}>
                            <span className={"cc-pf-unit__label"}>Loans</span>
                            <Select
                                size={"small"}
                                value={loanFilter || "non_progfin_loans"}
                                onChange={onLoanFilterChange}
                                dropdownMatchSelectWidth={false}
                            >
                                {LOAN_FILTER_OPTIONS.map(o => (
                                    <Option key={o.value} value={o.value}>
                                        {o.label}
                                    </Option>
                                ))}
                            </Select>
                        </span>
                        <span className={"cc-pf-unit"}>
                            <span className={"cc-pf-unit__label"}>
                                Include closed
                            </span>
                            <Switch
                                size={"small"}
                                checkedChildren={"Yes"}
                                unCheckedChildren={"No"}
                                checked={Boolean(includeClosed)}
                                onChange={onIncludeClosedChange}
                            />
                        </span>
                        <span className={"cc-pf-unit"}>
                            <span className={"cc-pf-unit__label"}>Show in</span>
                            <Select
                                size={"small"}
                                value={this.state.unit}
                                onChange={this.setUnit}
                                dropdownMatchSelectWidth={false}
                            >
                                {MONEY_UNIT_OPTIONS.map(o => (
                                    <Option key={o.key} value={o.key}>
                                        {o.label}
                                    </Option>
                                ))}
                            </Select>
                        </span>
                    </span>
                }
            >
                <Spin spinning={Boolean(loading)}>
                    {hasData ? (
                        <React.Fragment>
                            <OverallTiles
                                overall={data.overall}
                                unit={this.state.unit}
                                summary={this.props.summary}
                            />
                            {data.applicants.map(this.renderApplicant)}
                        </React.Fragment>
                    ) : (
                        !loading && (
                            <Empty
                                description={
                                    "No bureau loan obligations available for this case"
                                }
                            />
                        )
                    )}
                </Spin>
                {/* Eligibility Calculator — collapsed-by-default accordion at the
                    end of this section (Term Loan cases only; passed in by the
                    parent). CardBlock's own collapsible header is the accordion. */}
                {eligibilityCalculator ? (
                    <CardBlock
                        icon={"calculator"}
                        title={"Eligibility Calculator"}
                        className={"cc-eligibility"}
                        collapsible
                        defaultCollapsed
                    >
                        {eligibilityCalculator}
                    </CardBlock>
                ) : null}
                {this.renderAddModal()}
            </SectionBlock>
        );
    }
}

BureauLoanSection.propTypes = {
    innerRef: PropTypes.object,
    caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Gates "Refresh CIBIL" and "Add Loan" to the credit stages (7, 8).
    stageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data: PropTypes.object,
    summary: PropTypes.object,
    loading: PropTypes.bool,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    onRefresh: PropTypes.func,
    loanFilter: PropTypes.string,
    onLoanFilterChange: PropTypes.func,
    includeClosed: PropTypes.bool,
    onIncludeClosedChange: PropTypes.func,
    // Optional Eligibility Calculator node, rendered as a collapsible accordion
    // at the end of the section (Term Loan cases only).
    eligibilityCalculator: PropTypes.node,
    // Re-pull CIBIL for one applicant (company_associate id). When provided, a
    // "Trigger CIBIL" button shows for applicants whose last pull is >30d old.
    onTriggerCibil: PropTypes.func,
    // Applicant id whose CIBIL pull is currently in flight (spinner on its btn).
    triggeringCibilId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ])
};

export default BureauLoanSection;
